import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import QRCode from "qrcode";
import {
  Beer, Shirt, ShoppingBag, Coins, Bus, Target, UtensilsCrossed, Package,
  Plus, Minus, Pencil, Trash2, ArrowUp, ArrowDown, ArrowLeft, Eye, EyeOff, Wallet, QrCode, Copy, Download, PiggyBank,
} from "lucide-react";
import api, { API, purchaseItemImageUrl, withToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { dateLocale } from "@/lib/i18n";

// Module ACHATS — Phase 1 : catalogue. Phase 2 : ardoise (panier, historique, solde,
// règlements manuels espèces/chèque/virement, annulation tracée, idempotence anti double-clic).
const SHOP_ICONS = {
  beer: Beer, shirt: Shirt, "shopping-bag": ShoppingBag, coins: Coins,
  bus: Bus, target: Target, utensils: UtensilsCrossed, package: Package,
};
const SETTLE_METHODS = ["CASH", "CHECK", "TRANSFER"];
const STATUS_STYLE = {
  OPEN_TAB: "border-amber-500/30 bg-amber-500/10 text-amber-400",
  SETTLED: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  CANCELLED: "border-app t-secondary",
};

const fmtEUR = (cents, lang) =>
  (cents / 100).toLocaleString(dateLocale(lang), { style: "currency", currency: "EUR" });

export default function AchatsPage() {
  const { t, lang } = useAuth();
  // Module ACHATS temporairement désactivé — vitrine « en cours de développement » pour tous (jusqu'à nouvel ordre)
  return (
    <div className="flex-1 flex items-center justify-center py-16" data-testid="achats-under-dev">
      <div className="app-card p-8 max-w-md w-full text-center space-y-3">
        <span className="mx-auto h-12 w-12 rounded-xl brand-bg flex items-center justify-center">
          <ShoppingBag className="h-6 w-6 text-white" />
        </span>
        <h2 className="font-heading text-xl font-bold">{t("nav.purchases")}</h2>
        <p className="text-sm font-semibold accent" data-testid="achats-under-dev-badge">{t("achats.dev_title")}</p>
        <p className="text-sm t-secondary">{t("achats.dev_text")}</p>
      </div>
    </div>
  );
  const [catalog, setCatalog] = useState(null);
  const [mode, setMode] = useState("shop"); // shop | tab | finance | manage
  const [catId, setCatId] = useState(null);
  const [catForm, setCatForm] = useState(null);
  const [itemForm, setItemForm] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  // Phase 2 — ardoise
  const [tabData, setTabData] = useState(null);
  const [financeData, setFinanceData] = useState(null);
  const [finFilter, setFinFilter] = useState("OPEN_TAB");
  const [cart, setCart] = useState({}); // item_id -> qty
  const [cartKey, setCartKey] = useState(null); // clé d'idempotence du panier courant
  const [cartBusy, setCartBusy] = useState(false);
  const [settleTarget, setSettleTarget] = useState(null);
  const [settleForm, setSettleForm] = useState({ method: "CASH", amount: "", note: "" });
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [qrTarget, setQrTarget] = useState(null); // Phase 3 — {name, link} QR catégorie ou cagnotte
  // Phase 4 — cagnottes + cotisations
  const [pots, setPots] = useState(null);
  const [potForm, setPotForm] = useState(null); // {editing, name, description, goal}
  const [contribPot, setContribPot] = useState(null);
  const [contribAmount, setContribAmount] = useState("");
  const [contribKey, setContribKey] = useState(null);
  const [contribBusy, setContribBusy] = useState(false);
  // Verrou SYNCHRONE anti double-clic : le state React n'est pas commit entre des clics
  // synchrones (rafale JS) — seul un ref bloque réellement les envois concurrents.
  const busyRef = useRef(false);
  const genKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
  const [feeData, setFeeData] = useState(null);
  const [feesSummary, setFeesSummary] = useState(null);
  // Phase 5 — trésorerie
  const [stats, setStats] = useState(null);
  const [statsRange, setStatsRange] = useState({ from: "", to: "" });
  const [preset, setPreset] = useState("season");
  const [memberFilter, setMemberFilter] = useState("");
  const [financeMembers, setFinanceMembers] = useState([]);
  const [showAllRefunds, setShowAllRefunds] = useState(false);
  // Phase 6 — paiements en ligne (Stripe Connect, mode test)
  // MASQUÉ : rien ne passe par Stripe aujourd'hui — code + données conservés en base pour une réactivation future
  const STRIPE_ONLINE_PAYMENTS = false;
  const [payStatus, setPayStatus] = useState(null);
  const isLive = payStatus?.stripe_mode === "live";
  // Phase 7 — paiement sur place (QR éphémère, le membre paie sur son téléphone)
  const [onsiteOpen, setOnsiteOpen] = useState(false);
  const [onsiteCart, setOnsiteCart] = useState({});
  const [onsiteTx, setOnsiteTx] = useState(null); // {session_id, checkout_url, purchase_id, amount_cents}
  const [onsitePaid, setOnsitePaid] = useState(false);
  const onsiteQrRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/purchases/catalog").then((r) => setCatalog(r.data)).catch((e) => toast.error(e.friendly));
    api.get("/purchases/me").then((r) => setTabData(r.data)).catch(() => {});
    api.get("/purchases/pots").then((r) => setPots(r.data.pots || [])).catch(() => setPots([]));
    api.get("/fees/my").then((r) => setFeeData(r.data)).catch(() => {});
    api.get("/purchases/payments/connect/status").then((r) => setPayStatus(r.data)).catch(() => setPayStatus(null));
  }, []);
  const loadFinance = useCallback((status, member) => {
    const s = status ?? finFilter;
    const m = member ?? memberFilter;
    const params = {};
    if (s !== "ALL") params.status = s;
    if (m) params.member_id = m;
    api.get("/purchases", { params })
      .then((r) => {
        setFinanceData(r.data);
        if (!m) {
          const map = {};
          r.data.purchases.forEach((p) => { map[p.member_user_id] = p.member_name; });
          r.data.balances.forEach((b) => { map[b.member_user_id] = b.member_name; });
          setFinanceMembers(Object.entries(map));
        }
      })
      .catch((e) => toast.error(e.friendly));
    api.get("/fees").then((r) => setFeesSummary(r.data.summary ? { ...r.data.summary, period: r.data.period } : null)).catch(() => setFeesSummary(null));
  }, [finFilter, memberFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (mode === "finance") loadFinance(); }, [mode, loadFinance]);

  // Phase 3/4 — liens QR : ?cat=<id> ouvre la catégorie, ?pot=<id> ouvre la contribution (après login)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const cid = searchParams.get("cat");
    const pid = searchParams.get("pot");
    const pay = searchParams.get("payment");
    const conn = searchParams.get("connect");
    // Phase 6 — retour de la page Stripe hébergée
    if (pay === "success") {
      const sid = searchParams.get("session_id");
      setSearchParams({}, { replace: true });
      let tries = 0;
      const poll = async () => {
        try {
          const r = await api.get(`/purchases/payments/status/${sid}`);
          if (r.data.payment_status === "paid") {
            toast.success(t(isLive ? "shop.pay_success_live" : "shop.pay_success"));
            load();
          } else if (++tries < 10) {
            setTimeout(poll, 1500);
          }
        } catch { /* sondage best-effort */ }
      };
      if (sid) poll();
      return;
    }
    if (pay === "cancelled") {
      toast(t("shop.pay_cancelled"));
      setSearchParams({}, { replace: true });
      return;
    }
    if (conn === "done") {
      toast.success(t("shop.connect_returned"));
      setMode("treasury");
      api.get("/purchases/payments/connect/status").then((r) => setPayStatus(r.data)).catch(() => {});
      setSearchParams({}, { replace: true });
      return;
    }
    if (cid && catalog) {
      if ((catalog.categories || []).some((c) => c.id === cid)) {
        setMode("shop");
        setCatId(cid);
      } else {
        toast.error(t("shop.qr_invalid")); // catégorie d'un autre groupe ou désactivée
      }
      setSearchParams({}, { replace: true });
    } else if (pid && pots !== null) {
      const pot = pots.find((p) => p.id === pid && p.active);
      if (pot) {
        setMode("shop");
        setContribPot(pot);
        setContribAmount("");
        setContribKey(genKey());
      } else {
        toast.error(t("shop.qr_invalid"));
      }
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, pots]);

  // ---------- Phase 5 : trésorerie ----------
  const loadStats = useCallback((range) => {
    const r = range ?? statsRange;
    const params = {};
    if (r.from) params.date_from = r.from;
    if (r.to) params.date_to = r.to;
    api.get("/purchases/stats", { params }).then((res) => setStats(res.data)).catch((e) => toast.error(e.friendly));
  }, [statsRange]);

  const todayStr = () => new Date().toISOString().slice(0, 10);
  const monthStart = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-01`; };
  const seasonStart = () => { const n = new Date(); const y = n.getMonth() >= 8 ? n.getFullYear() : n.getFullYear() - 1; return `${y}-09-01`; }; // saison sept → juin

  const applyPreset = (p) => {
    const r = p === "month" ? { from: monthStart(), to: todayStr() } : p === "season" ? { from: seasonStart(), to: todayStr() } : { from: "", to: "" };
    setPreset(p);
    setStatsRange(r);
    loadStats(r);
  };

  useEffect(() => {
    // recharge à CHAQUE entrée dans l'onglet : des encaissements/annulations ont pu survenir ailleurs
    if (mode === "treasury") loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Phase 5 — remboursement conceptuel : achat annulé après encaissement, marqué remboursé (tracé)
  const doRefund = async (p) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      await api.post(`/purchases/${p.id}/refunded`);
      toast.success(t("shop.refunded_done"));
      loadStats();
      loadFinance();
    } catch (e) { toast.error(e.friendly); } finally { busyRef.current = false; }
  };

  const exportHref = () => {
    const q = [];
    if (statsRange.from) q.push(`date_from=${statsRange.from}`);
    if (statsRange.to) q.push(`date_to=${statsRange.to}`);
    return withToken(`${API}/purchases/export.csv${q.length ? `?${q.join("&")}` : ""}`);
  };

  // ---------- Phase 6 : paiements en ligne (Stripe Connect, MODE TEST) ----------
  // Redirection vers la page Stripe hébergée — aucune donnée bancaire ne transite ici.
  const goStripe = async (payload) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const r = await api.post("/purchases/payments/checkout", { ...payload, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.friendly);
      busyRef.current = false;
    }
  };
  const doConnect = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const r = await api.post("/purchases/payments/connect", { origin_url: window.location.origin });
      if (r.data.onboarding_url) {
        window.location.href = r.data.onboarding_url; // onboarding Stripe hébergé
      } else {
        // mode test partagé : pas d'onboarding, activation immédiate
        toast.success(t("shop.pay_test_shared"));
        const s = await api.get("/purchases/payments/connect/status");
        setPayStatus(s.data);
        busyRef.current = false;
      }
    } catch (e) {
      toast.error(e.friendly);
      busyRef.current = false;
    }
  };
  const payTabOnline = () => goStripe({ kind: "TAB" });
  const payPotOnline = () => {
    const amount = parseFloat(String(contribAmount).replace(",", "."));
    if (!contribPot || isNaN(amount) || amount < 0.5) return;
    goStripe({ kind: "POT", pot_id: contribPot.id, amount });
  };
  const buyItemOnline = (item) => goStripe({ kind: "ITEM", item_id: item.id, qty: 1 });

  // ---------- Phase 7 : paiement sur place (vente comptoir) ----------
  const onsiteLines = Object.entries(onsiteCart).filter(([, q]) => q > 0);
  const onsiteBump = (id, d) => setOnsiteCart((c) => ({ ...c, [id]: Math.max(0, Math.min(50, (c[id] || 0) + d)) }));
  const onsiteTotal = () => onsiteLines.reduce((s, [id, q]) => s + q * priceOf(id), 0);

  const generateOnsite = async () => {
    if (!onsiteLines.length || busyRef.current) return;
    busyRef.current = true;
    try {
      const r = await api.post("/purchases/payments/on-site", {
        lines: onsiteLines.map(([item_id, qty]) => ({ item_id, qty })),
        origin_url: window.location.origin,
      });
      setOnsiteTx(r.data);
      setOnsitePaid(false);
    } catch (e) { toast.error(e.friendly); } finally { busyRef.current = false; }
  };

  // QR de la session Checkout (rendu haute résolution, affichage borné mobile)
  useEffect(() => {
    if (onsiteTx && onsiteQrRef.current) {
      QRCode.toCanvas(onsiteQrRef.current, onsiteTx.checkout_url, { width: 640, margin: 2 })
        .then(() => { if (onsiteQrRef.current) { onsiteQrRef.current.style.width = "240px"; onsiteQrRef.current.style.height = "240px"; } })
        .catch(() => {});
    }
  }, [onsiteTx]);

  // Sondage du statut tant que la vente est en cours (même endpoint idempotent que le retour membre)
  useEffect(() => {
    if (!onsiteTx || onsitePaid || !onsiteOpen) return;
    let stop = false;
    let timer = null;
    const poll = async () => {
      if (stop) return;
      try {
        const r = await api.get(`/purchases/payments/status/${onsiteTx.session_id}`);
        if (r.data.payment_status === "paid") {
          setOnsitePaid(true);
          toast.success(t("shop.onsite_paid"));
          load();
          loadFinance();
          return;
        }
      } catch { /* best-effort */ }
      if (!stop) timer = setTimeout(poll, 2000);
    };
    timer = setTimeout(poll, 2000);
    return () => { stop = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onsiteTx, onsitePaid, onsiteOpen]);

  const closeOnsite = async () => {
    // vente non payée abandonnée → annulation tracée (jamais de dette fantôme)
    if (onsiteTx && !onsitePaid) {
      try { await api.post(`/purchases/${onsiteTx.purchase_id}/cancel`, { reason: t("shop.onsite_abandon_reason") }); } catch { /* déjà réglée/annulée */ }
      loadFinance();
    }
    setOnsiteOpen(false);
    setOnsiteTx(null);
    setOnsitePaid(false);
    setOnsiteCart({});
  };

  const newOnsiteSale = () => {
    setOnsiteTx(null);
    setOnsitePaid(false);
    setOnsiteCart({});
  };

  const canManage = !!catalog?.can_manage;
  const categories = catalog?.categories || [];
  const selected = categories.find((c) => c.id === catId) || null;
  const euro = (cents) => fmtEUR(cents ?? 0, lang);

  // ---------- Panier / ardoise ----------

  const cartLines = useMemo(() => Object.entries(cart).filter(([, q]) => q > 0), [cart]);
  const cartCount = cartLines.reduce((s, [, q]) => s + q, 0);
  const priceOf = (itemId) => {
    for (const c of categories) {
      const it = c.items.find((i) => i.id === itemId);
      if (it) return it.price_cents;
    }
    return 0;
  };
  const cartTotal = cartLines.reduce((s, [id, q]) => s + q * priceOf(id), 0);
  const bump = (id, d) => setCart((c) => ({ ...c, [id]: Math.max(0, Math.min(50, (c[id] || 0) + d)) }));

  const submitCart = async () => {
    if (!cartLines.length || cartBusy || busyRef.current) return;
    // La clé est conservée tant que la requête n'a pas abouti : une nouvelle tentative
    // (double-clic, réseau coupé) renvoie l'achat existant au lieu d'en créer un second.
    const key = cartKey || genKey();
    setCartKey(key);
    setCartBusy(true);
    busyRef.current = true;
    try {
      await api.post("/purchases", {
        lines: cartLines.map(([item_id, qty]) => ({ item_id, qty })),
        idempotency_key: key,
      });
      toast.success(t("shop.cart_added"));
      setCart({});
      setCartKey(null);
      load();
    } catch (e) { toast.error(e.friendly); } finally { setCartBusy(false); busyRef.current = false; }
  };

  const openSettle = (p) => {
    setSettleTarget(p);
    setSettleForm({ method: "CASH", amount: (p.remaining_cents / 100).toFixed(2), note: "" });
  };
  // Phase 4 — contribution à une cagnotte (montant libre sur l'ardoise, idempotent)
  const submitContrib = async () => {
    const amount = parseFloat(String(contribAmount).replace(",", "."));
    if (!contribPot || isNaN(amount) || amount <= 0 || contribBusy || busyRef.current) return;
    // Clé générée à l'OUVERTURE du dialog : tous les clics de la même session de saisie
    // partagent la même clé → le backend renvoie l'achat existant en cas de rejoue.
    const key = contribKey || genKey();
    setContribKey(key);
    setContribBusy(true);
    busyRef.current = true;
    try {
      await api.post(`/purchases/pots/${contribPot.id}/contribute`, { amount, idempotency_key: key });
      toast.success(t("shop.pot_contributed"));
      setContribPot(null);
      setContribAmount("");
      setContribKey(null);
      load();
    } catch (e) { toast.error(e.friendly); } finally { setContribBusy(false); busyRef.current = false; }
  };

  const savePot = async () => {
    if (!potForm.name.trim()) return;
    if (busyRef.current) return;
    busyRef.current = true;
    setSaving(true);
    try {
      const payload = {
        name: potForm.name.trim(),
        description: potForm.description.trim(),
        goal: potForm.goal === "" ? null : parseFloat(String(potForm.goal).replace(",", ".")) || 0,
      };
      if (potForm.editing) {
        await api.put(`/purchases/pots/${potForm.editing.id}`, payload);
        toast.success(t("shop.pot_updated"));
      } else {
        await api.post("/purchases/pots", payload);
        toast.success(t("shop.pot_created"));
      }
      setPotForm(null);
      load();
    } catch (e) { toast.error(e.friendly); } finally { setSaving(false); busyRef.current = false; }
  };

  const togglePot = async (p) => {
    try {
      await api.put(`/purchases/pots/${p.id}/active?active=${!p.active}`);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const doSettle = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setSaving(true);
    try {
      const amount = parseFloat(String(settleForm.amount).replace(",", "."));
      await api.post(`/purchases/${settleTarget.id}/settle`, {
        amount: isNaN(amount) ? null : amount,
        method: settleForm.method,
        note: settleForm.note,
      });
      toast.success(t("shop.settle_done"));
      setSettleTarget(null);
      load();
      loadFinance();
    } catch (e) { toast.error(e.friendly); } finally { setSaving(false); busyRef.current = false; }
  };

  const doCancel = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setSaving(true);
    try {
      await api.post(`/purchases/${cancelTarget.id}/cancel`, { reason: cancelReason });
      toast.success(t("shop.cancel_done"));
      setCancelTarget(null);
      setCancelReason("");
      load();
      loadFinance();
    } catch (e) { toast.error(e.friendly); } finally { setSaving(false); busyRef.current = false; }
  };

  // ---------- Actions de gestion (Phase 1, inchangé) ----------

  const saveCategory = async () => {
    if (!catForm.name.trim()) return;
    if (busyRef.current) return;
    busyRef.current = true;
    setSaving(true);
    try {
      const payload = {
        name: catForm.name.trim(),
        icon: catForm.icon,
        allow_tab: catForm.allow_tab,
        allow_immediate_payment: catForm.allow_immediate_payment,
      };
      if (catForm.editing) {
        await api.put(`/purchases/categories/${catForm.editing.id}`, payload);
        toast.success(t("shop.cat_updated"));
      } else {
        await api.post("/purchases/categories", payload);
        toast.success(t("shop.cat_created"));
      }
      setCatForm(null);
      load();
    } catch (e) { toast.error(e.friendly); } finally { setSaving(false); busyRef.current = false; }
  };

  const toggleCategory = async (c) => {
    try {
      await api.put(`/purchases/categories/${c.id}/active?active=${!c.active}`);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const moveCategory = async (idx, dir) => {
    const ids = categories.map((c) => c.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    await api.post("/purchases/categories/reorder", { ids }).catch((e) => toast.error(e.friendly));
    load();
  };

  const saveItem = async () => {
    if (!itemForm.name.trim() || itemForm.price === "") return;
    if (busyRef.current) return;
    busyRef.current = true;
    setSaving(true);
    try {
      const payload = {
        category_id: itemForm.category_id,
        name: itemForm.name.trim(),
        description: itemForm.description.trim(),
        price: parseFloat(String(itemForm.price).replace(",", ".")) || 0,
        allow_tab: itemForm.allow_tab === "inherit" ? null : itemForm.allow_tab === "yes",
        active: itemForm.active,
      };
      let itemId = itemForm.editing?.id;
      if (itemForm.editing) {
        await api.put(`/purchases/items/${itemForm.editing.id}`, payload);
        toast.success(t("shop.item_updated"));
      } else {
        const r = await api.post("/purchases/items", payload);
        itemId = r.data.id;
        toast.success(t("shop.item_created"));
      }
      if (itemForm.image && itemId) {
        const fd = new FormData();
        fd.append("file", itemForm.image);
        await api.post(`/purchases/items/${itemId}/image`, fd).catch((e) => toast.error(e.friendly));
      }
      setItemForm(null);
      load();
    } catch (e) { toast.error(e.friendly); } finally { setSaving(false); busyRef.current = false; }
  };

  const moveItem = async (cat, idx, dir) => {
    const ids = cat.items.map((i) => i.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    await api.post("/purchases/items/reorder", { ids }).catch((e) => toast.error(e.friendly));
    load();
  };

  const doDelete = async () => {
    try {
      const seg = delTarget.type === "category" ? "categories" : delTarget.type === "pot" ? "pots" : "items";
      await api.delete(`/purchases/${seg}/${delTarget.obj.id}`);
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(e.friendly); // 409 : historique/contributions → désactiver ou clôturer plutôt
      setDelTarget(null);
    }
  };

  // ---------- Composants ----------

  const statusLabel = (s) => t(s === "OPEN_TAB" ? "shop.tab_open" : s === "SETTLED" ? "shop.tab_settled" : "shop.tab_cancelled");
  const FEE_STATUS_STYLE = {
    paid: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
    due: "border-amber-500/30 bg-amber-500/10 text-amber-400",
    pending: "border-amber-500/30 bg-amber-500/10 text-amber-400",
    exempt: "border-app t-secondary",
    not_applicable: "border-app t-secondary",
  };
  // Phase 4 — cotisation de la période en cours (module Cotisations existant, lecture seule)
  const currentFee = feeData?.enabled
    ? (feeData.fees || []).find((f) => f.period_id === feeData.current_period_id) || null
    : null;

  const ItemCard = ({ item, cat }) => {
    const tabOk = item.allow_tab ?? cat.allow_tab;
    return (
      <div className={`app-card p-4 flex items-center gap-x-4 gap-y-2 flex-wrap ${!item.active ? "opacity-50" : ""}`} data-testid={`shop-item-${item.id}`}>
        {/* Mobile : image+nom sur une ligne pleine, prix+contrôles alignés à droite en dessous */}
        <div className="flex items-center gap-4 min-w-0 flex-1 basis-full sm:basis-auto">
          {item.image_path ? (
            <img src={purchaseItemImageUrl(item.id)} alt={item.name} className="w-14 h-14 rounded-xl object-cover border border-app shrink-0" data-testid={`shop-item-img-${item.id}`} />
          ) : (
            <span className="w-14 h-14 rounded-xl card-bg border border-app flex items-center justify-center shrink-0">
              {(() => { const Icon = SHOP_ICONS[cat.icon] || Package; return <Icon className="h-6 w-6 accent" />; })()}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <p className="font-semibold break-words">{item.name}</p>
            {item.description && <p className="text-xs t-secondary break-words">{item.description}</p>}
            <div className="flex gap-1.5 mt-1 flex-wrap">
              {tabOk && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-400">{t("shop.tab_badge")}</span>}
              {cat.allow_immediate_payment && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">{t("shop.pay_badge")}</span>}
              {!item.active && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-app t-secondary">{t("shop.inactive_badge")}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 max-sm:w-full max-sm:justify-end">
          <p className="font-heading font-bold text-lg" data-testid={`shop-item-price-${item.id}`}>{euro(item.price_cents)}</p>
          {STRIPE_ONLINE_PAYMENTS && cat.allow_immediate_payment && item.active && payStatus?.enabled && (
            <Button size="sm" className="bg-sky-600 hover:bg-sky-500 text-white" onClick={() => buyItemOnline(item)} data-testid={`item-buy-${item.id}`}>{t("shop.buy_now")}</Button>
          )}
          {tabOk && item.active && (
            <div className="flex items-center gap-1.5" data-testid={`item-cart-${item.id}`}>
              <button className="p-1.5 rounded-full border border-app t-secondary hover:text-white disabled:opacity-30" disabled={!cart[item.id]} onClick={() => bump(item.id, -1)} data-testid={`item-qty-minus-${item.id}`} aria-label="−">
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="w-5 text-center text-sm font-semibold" data-testid={`item-qty-${item.id}`}>{cart[item.id] || 0}</span>
              <button className="p-1.5 rounded-full brand-bg text-white" onClick={() => bump(item.id, 1)} data-testid={`item-qty-plus-${item.id}`} aria-label="+">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const PurchaseCard = ({ p, finance }) => (
    <div className={`app-card p-4 ${p.status === "CANCELLED" ? "opacity-60" : ""}`} data-testid={`purchase-${p.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {finance && <span className="font-semibold" data-testid={`purchase-member-${p.id}`}>{p.member_name}</span>}
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_STYLE[p.status]}`} data-testid={`purchase-status-${p.id}`}>{statusLabel(p.status)}</span>
          </div>
          <p className="text-xs t-secondary mt-1">
            {new Date(p.created_at).toLocaleString(dateLocale(lang), { dateStyle: "medium", timeStyle: "short" })}
          </p>
          <ul className="text-sm mt-2 space-y-0.5">
            {p.lines.map((l, i) => (
              <li key={i}>{l.qty} × {l.name} <span className="t-secondary">({euro(l.unit_price_cents * l.qty)})</span></li>
            ))}
          </ul>
          {p.settlements?.length > 0 && (
            <p className="text-xs t-secondary mt-2">
              {t("shop.settlements")} : {p.settlements.map((s) => `${euro(s.amount_cents)} ${t(`shop.method_${s.method.toLowerCase()}`)}`).join(" • ")}
            </p>
          )}
          {p.status === "CANCELLED" && p.cancel_reason && (
            <p className="text-xs text-rose-400 mt-2">{t("shop.cancelled_by", { name: p.cancelled_by || "—" })} : {p.cancel_reason}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="font-heading font-bold text-lg" data-testid={`purchase-total-${p.id}`}>{euro(p.total_cents)}</p>
          {p.status === "OPEN_TAB" && p.paid_cents > 0 && (
            <p className="text-xs t-secondary">{t("shop.remaining")} : {euro(p.remaining_cents)}</p>
          )}
          {finance && p.status === "OPEN_TAB" && (
            <div className="flex gap-1.5 mt-2 justify-end flex-wrap">
              <Button size="sm" className="brand-bg text-white" onClick={() => openSettle(p)} data-testid={`settle-btn-${p.id}`}>{t("shop.settle")}</Button>
              <Button size="sm" variant="outline" className="border-app" onClick={() => { setCancelTarget(p); setCancelReason(""); }} data-testid={`cancel-purchase-btn-${p.id}`}>{t("shop.cancel_purchase")}</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Phase 3/4 — panneau QR : lien direct (catégorie ou cagnotte), rendu canvas haute résolution
  const QrPanel = ({ target }) => {
    const canvasRef = useRef(null);
    const link = target.link;
    useEffect(() => {
      if (canvasRef.current) {
        QRCode.toCanvas(canvasRef.current, link, { width: 640, margin: 2, color: { dark: "#0b1220", light: "#ffffff" } })
          .then(() => {
            // la lib fixe la taille intrinsèque (640) — forcer l'affichage mobile
            canvasRef.current.style.width = "208px";
            canvasRef.current.style.height = "208px";
          })
          .catch(() => {});
      }
    }, [link]);
    const download = () => {
      if (!canvasRef.current) return;
      const a = document.createElement("a");
      a.href = canvasRef.current.toDataURL("image/png");
      a.download = `qr-${target.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
      a.click();
    };
    const copy = async () => {
      try {
        await navigator.clipboard.writeText(link);
        toast.success(t("lib.link_copied"));
      } catch {
        window.prompt(link);
      }
    };
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-2xl p-4 flex items-center justify-center overflow-hidden">
          <canvas ref={canvasRef} style={{ width: 208, height: 208 }} data-testid="qr-canvas" />
        </div>
        <p className="text-xs t-secondary">{t("shop.qr_hint")}</p>
        <p className="text-[11px] t-secondary break-all" data-testid="qr-link-text">{link}</p>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 border-app" onClick={copy} data-testid="qr-copy">
            <Copy className="h-4 w-4 mr-1" /> {t("shop.qr_copy")}
          </Button>
          <Button className="flex-1 brand-bg text-white" onClick={download} data-testid="qr-download">
            <Download className="h-4 w-4 mr-1" /> {t("shop.qr_download")}
          </Button>
        </div>
      </div>
    );
  };

  if (!catalog) {
    return <p className="t-secondary text-sm py-10 text-center" data-testid="shop-loading">{t("agd.loading")}</p>;
  }

  const modeBtn = (m) => `px-3 ${mode === m ? "brand-bg text-white" : "border-app"}`;

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="shop-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl font-bold">{mode === "manage" ? t("shop.manage") : t("shop.title")}</h2>
          <p className="text-sm t-secondary mt-1">{t("shop.subtitle")}</p>
        </div>
        {tabData && tabData.balance_cents > 0 && (
          <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-400" data-testid="shop-balance-badge">
            <Wallet className="h-3.5 w-3.5" /> {t("shop.balance_due")} : {euro(tabData.balance_cents)}
          </span>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button variant={mode === "shop" ? "default" : "outline"} className={modeBtn("shop")} onClick={() => setMode("shop")} data-testid="shop-mode-shop">
          {t("shop.catalog")}
        </Button>
        <Button variant={mode === "tab" ? "default" : "outline"} className={modeBtn("tab")} onClick={() => setMode("tab")} data-testid="shop-mode-tab">
          {t("shop.tab_title")}
        </Button>
        {canManage && (
          <>
            <Button variant={mode === "finance" ? "default" : "outline"} className={modeBtn("finance")} onClick={() => setMode("finance")} data-testid="shop-mode-finance">
              {t("shop.finance_view")}
            </Button>
            <Button variant={mode === "treasury" ? "default" : "outline"} className={modeBtn("treasury")} onClick={() => setMode("treasury")} data-testid="shop-mode-treasury">
              {t("shop.treasury")}
            </Button>
            <Button variant={mode === "manage" ? "default" : "outline"} className={modeBtn("manage")} onClick={() => { setMode("manage"); setCatId(null); }} data-testid="shop-mode-manage">
              {t("shop.manage_short")}
            </Button>
          </>
        )}
      </div>

      {/* ---------- BOUTIQUE (membre) ---------- */}
      {mode === "shop" && !selected && (
        categories.length === 0 ? (
          <p className="t-secondary text-sm" data-testid="shop-empty">{t("shop.empty_catalog")}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="shop-categories">
            {categories.map((c) => {
              const Icon = SHOP_ICONS[c.icon] || Package;
              return (
                <button key={c.id} onClick={() => setCatId(c.id)} className="app-card p-5 flex flex-col items-center gap-2.5 hover:bg-white/[0.04] active:bg-white/[0.06] transition-colors" data-testid={`shop-cat-${c.id}`}>
                  <Icon className="h-9 w-9 accent" />
                  <span className="font-semibold text-sm">{c.name}</span>
                  <span className="text-xs t-secondary">{t("shop.items_count", { count: c.items.length })}</span>
                </button>
              );
            })}
          </div>
        )
      )}

      {/* ---------- CAGNOTTES (membre, Phase 4) ---------- */}
      {mode === "shop" && !selected && (pots || []).some((p) => p.active) && (
        <div className="space-y-3" data-testid="shop-pots">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2">
            <PiggyBank className="h-5 w-5 accent" /> {t("shop.pots_title")}
          </h3>
          {(pots || []).filter((p) => p.active).map((pot) => {
            const pct = pot.goal_cents ? Math.min(100, Math.round((100 * pot.collected_cents) / pot.goal_cents)) : null;
            const reached = !!pot.goal_cents && pot.collected_cents >= pot.goal_cents;
            return (
              <div key={pot.id} className="app-card p-4" data-testid={`pot-${pot.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                    <p className="font-semibold break-words">{pot.name}</p>
                    {pot.description && <p className="text-xs t-secondary break-words mt-0.5">{pot.description}</p>}
                    <p className="text-xs mt-1.5">
                      <span className={reached ? "text-emerald-400 font-semibold" : "t-secondary"} data-testid={`pot-collected-${pot.id}`}>
                        {t("shop.pot_collected", { amount: euro(pot.collected_cents) })}
                      </span>
                      <span className="t-secondary"> • {pot.goal_cents ? t("shop.pot_goal_label", { amount: euro(pot.goal_cents) }) : t("shop.pot_free")}</span>
                      {reached && <span className="ml-1.5 text-emerald-400 font-semibold">{t("shop.pot_goal_reached")}</span>}
                    </p>
                  </div>
                  <Button size="sm" className="brand-bg text-white shrink-0" onClick={() => { setContribPot(pot); setContribAmount(""); setContribKey(genKey()); }} data-testid={`pot-contribute-${pot.id}`}>
                    {t("shop.pot_contribute")}
                  </Button>
                </div>
                {pot.goal_cents ? (
                  <div className="h-1.5 mt-3 rounded-full bg-white/10 overflow-hidden">
                    <div className={`h-full rounded-full ${reached ? "bg-emerald-400" : "brand-bg"}`} style={{ width: `${pct}%` }} data-testid={`pot-progress-${pot.id}`} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {mode === "shop" && selected && (
        <div className="space-y-3" data-testid="shop-category-view">
          <button onClick={() => setCatId(null)} className="flex items-center gap-1.5 text-sm t-secondary hover:text-white transition-colors" data-testid="shop-cat-back">
            <ArrowLeft className="h-4 w-4" /> {t("shop.back_catalog")}
          </button>
          <h3 className="font-heading text-xl font-bold" data-testid="shop-category-title">{selected.name}</h3>
          {selected.items.length === 0 && <p className="t-secondary text-sm">{t("shop.empty_items")}</p>}
          {selected.items.map((item) => <ItemCard key={item.id} item={item} cat={selected} />)}
        </div>
      )}

      {/* ---------- MON ARDOISE (membre) ---------- */}
      {mode === "tab" && (
        <div className="space-y-3" data-testid="shop-tab-view">
          {currentFee && (
            <div className="app-card p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="my-fee-card">
              <div className="min-w-0">
                <p className="text-sm t-secondary">{t("shop.my_fee")}{currentFee.period_name ? ` — ${currentFee.period_name}` : ""}</p>
                <p className="font-heading font-bold text-lg" data-testid="my-fee-amount">{euro(currentFee.amount_due_cents)}</p>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${FEE_STATUS_STYLE[currentFee.status] || "border-app t-secondary"}`} data-testid="my-fee-status">
                {t(`fees.status_${currentFee.status}`)}
              </span>
            </div>
          )}
          <div className="app-card p-4 flex items-center justify-between">
            <span className="text-sm t-secondary">{t("shop.balance_due")}</span>
            <span className={`font-heading font-bold text-xl ${tabData?.balance_cents > 0 ? "text-amber-400" : "text-emerald-400"}`} data-testid="tab-balance">
              {tabData ? (tabData.balance_cents > 0 ? euro(tabData.balance_cents) : t("shop.balance_none")) : "…"}
            </span>
          </div>
          {STRIPE_ONLINE_PAYMENTS && tabData?.balance_cents > 0 && payStatus?.enabled && (
            <Button className="w-full brand-bg text-white" onClick={payTabOnline} data-testid="pay-tab-btn">
              {t(isLive ? "shop.pay_tab_live" : "shop.pay_tab")} — {euro(tabData.balance_cents)}
            </Button>
          )}
          {tabData && tabData.purchases.length === 0 && <p className="t-secondary text-sm" data-testid="tab-empty">{t("shop.tab_empty")}</p>}
          {(tabData?.purchases || []).map((p) => <PurchaseCard key={p.id} p={p} finance={false} />)}
        </div>
      )}

      {/* ---------- ENCAISSEMENTS (trésorier) ---------- */}
      {mode === "finance" && canManage && (
        <div className="space-y-3" data-testid="shop-finance-view">
          {STRIPE_ONLINE_PAYMENTS && payStatus?.enabled && (
            <Button className="w-full bg-sky-600 hover:bg-sky-500 text-white" onClick={() => { setOnsiteCart({}); setOnsiteTx(null); setOnsitePaid(false); setOnsiteOpen(true); }} data-testid="onsite-open">
              <QrCode className="h-4 w-4 mr-1.5" /> {t("shop.onsite")}
            </Button>
          )}
          {feesSummary && (
            <div className="app-card p-4" data-testid="fees-summary-card">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="t-secondary text-xs uppercase tracking-wider">
                  {t("shop.fees_summary")}{feesSummary.period?.name ? ` — ${feesSummary.period.name}` : ""}
                </p>
                <button className="text-xs brand hover:underline" onClick={() => navigate("/utilisateurs")} data-testid="fees-goto">
                  {t("shop.fees_goto")}
                </button>
              </div>
              <div className="flex gap-x-4 gap-y-1 mt-2 text-sm flex-wrap">
                <span>{t("fees.expected")} : <strong>{euro(feesSummary.expected_cents)}</strong></span>
                <span className="text-emerald-400">{t("fees.received")} : <strong>{euro(feesSummary.received_cents)}</strong></span>
                <span className="text-amber-400">{t("fees.remaining")} : <strong>{euro(feesSummary.remaining_cents)}</strong></span>
              </div>
            </div>
          )}
          {financeData && financeData.balances.length > 0 && (
            <div className="app-card p-4">
              <p className="t-secondary text-xs uppercase tracking-wider mb-2">{t("shop.open_tabs")}</p>
              <div className="flex flex-wrap gap-2">
                {financeData.balances.map((b) => (
                  <span key={b.member_user_id} className="text-xs font-semibold px-2.5 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-400" data-testid={`balance-${b.member_user_id}`}>
                    {b.member_name} : {euro(b.balance_cents)}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-1.5 flex-wrap">
            {[["OPEN_TAB", t("shop.tab_open")], ["SETTLED", t("shop.tab_settled")], ["CANCELLED", t("shop.tab_cancelled")], ["ALL", t("shop.all_status")]].map(([v, l]) => (
              <button key={v} onClick={() => { setFinFilter(v); loadFinance(v); }} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${finFilter === v ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`} data-testid={`fin-filter-${v.toLowerCase()}`}>
                {l}
              </button>
            ))}
          </div>
          {financeMembers.length > 1 && (
            <select value={memberFilter} onChange={(e) => { setMemberFilter(e.target.value); loadFinance(undefined, e.target.value); }} className="bg-transparent border border-app rounded-full text-xs px-3 py-1.5 t-secondary" data-testid="fin-filter-member">
              <option value="">{t("shop.filter_member")}</option>
              {financeMembers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          )}
          {financeData && financeData.purchases.length === 0 && <p className="t-secondary text-sm" data-testid="finance-empty">{t("shop.no_open_tabs")}</p>}
          {(financeData?.purchases || []).map((p) => <PurchaseCard key={p.id} p={p} finance />)}
        </div>
      )}

      {/* ---------- TRÉSORERIE (trésorier, Phase 5) ---------- */}
      {mode === "treasury" && canManage && (
        <div className="space-y-3" data-testid="shop-treasury-view">
          <div className="flex gap-1.5 flex-wrap items-center" data-testid="treasury-presets">
            {[["month", t("shop.period_month")], ["season", t("shop.period_season")], ["all", t("shop.period_all")]].map(([v, l]) => (
              <button key={v} onClick={() => applyPreset(v)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${preset === v ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`} data-testid={`treasury-preset-${v}`}>
                {l}
              </button>
            ))}
            <span className="flex items-center gap-1.5 text-xs t-secondary flex-wrap">
              {t("shop.date_from")}
              <input type="date" value={statsRange.from} onChange={(e) => { const r = { ...statsRange, from: e.target.value }; setStatsRange(r); setPreset("custom"); loadStats(r); }} className="bg-transparent border border-app rounded-lg px-2 py-1" data-testid="treasury-from" />
              {t("shop.date_to")}
              <input type="date" value={statsRange.to} onChange={(e) => { const r = { ...statsRange, to: e.target.value }; setStatsRange(r); setPreset("custom"); loadStats(r); }} className="bg-transparent border border-app rounded-lg px-2 py-1" data-testid="treasury-to" />
            </span>
          </div>
          {stats && (
            <>
              <div className="grid grid-cols-3 gap-2" data-testid="treasury-kpis">
                <div className="app-card p-3 text-center">
                  <p className="text-[10px] t-secondary uppercase tracking-wider">{t("shop.received_period")}</p>
                  <p className="font-heading font-bold text-lg text-emerald-400 break-words" data-testid="kpi-received">{euro(stats.received_cents)}</p>
                </div>
                <div className="app-card p-3 text-center">
                  <p className="text-[10px] t-secondary uppercase tracking-wider">{t("shop.open_tabs_total")}</p>
                  <p className="font-heading font-bold text-lg text-amber-400" data-testid="kpi-open">{euro(stats.open_total_cents)}</p>
                </div>
                <div className="app-card p-3 text-center">
                  <p className="text-[10px] t-secondary uppercase tracking-wider">{t("shop.refunds_due")}</p>
                  <p className="font-heading font-bold text-lg text-rose-400" data-testid="kpi-refunds">{euro(stats.refunds.reduce((s, r) => s + r.refund_due_cents, 0))}</p>
                </div>
              </div>
              <p className="text-xs t-secondary" data-testid="treasury-sales">
                {t("shop.sales_count", { count: stats.sales_count, amount: euro(stats.sales_total_cents) })}
              </p>

              <div className="app-card p-4 space-y-2" data-testid="treasury-methods">
                <p className="t-secondary text-xs uppercase tracking-wider">{t("shop.by_method")}</p>
                {SETTLE_METHODS.concat(["ONLINE"]).map((m) => {
                  const v = stats.by_method[m] || 0;
                  const pct = stats.received_cents ? Math.round((100 * v) / stats.received_cents) : 0;
                  return (
                    <div key={m} className="flex items-center gap-2 text-sm" data-testid={`treasury-method-${m.toLowerCase()}`}>
                      <span className="w-20 shrink-0 text-xs">{t(`shop.method_${m.toLowerCase()}`)}</span>
                      <div className="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full brand-bg" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-20 text-right text-xs font-semibold shrink-0">{euro(v)}</span>
                    </div>
                  );
                })}
              </div>

              {stats.debtors.length > 0 && (
                <div className="app-card p-4" data-testid="treasury-debtors">
                  <p className="t-secondary text-xs uppercase tracking-wider mb-2">{t("shop.debtors")}</p>
                  <div className="flex flex-wrap gap-2">
                    {stats.debtors.map((b) => (
                      <span key={b.member_user_id} className="text-xs font-semibold px-2.5 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-400" data-testid={`debtor-${b.member_user_id}`}>
                        {b.member_name} : {euro(b.balance_cents)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="app-card p-4 space-y-2" data-testid="treasury-refunds">
                <p className="t-secondary text-xs uppercase tracking-wider">{t("shop.refunds_due")}</p>
                {stats.refunds.length === 0 && <p className="t-secondary text-sm" data-testid="refunds-empty">{t("shop.no_refunds")}</p>}
                {(showAllRefunds ? stats.refunds : stats.refunds.slice(0, 5)).map((p) => (
                  <div key={p.id} className="flex items-center gap-3 text-sm flex-wrap" data-testid={`refund-${p.id}`}>
                    <span className="min-w-0 flex-1 basis-full sm:basis-auto break-words">
                      {t("shop.refund_to", { name: p.member_name })} — {p.lines.map((l) => l.name).join(", ")}
                    </span>
                    <span className="font-semibold shrink-0">{euro(p.refund_due_cents)}</span>
                    <Button size="sm" variant="outline" className="border-app" onClick={() => doRefund(p)} data-testid={`refund-btn-${p.id}`}>
                      {t("shop.mark_refunded")}
                    </Button>
                  </div>
                ))}
                {stats.refunds.length > 5 && (
                  <button className="text-xs brand hover:underline" onClick={() => setShowAllRefunds(!showAllRefunds)} data-testid="refunds-toggle">
                    {showAllRefunds ? t("shop.refunds_show_less") : t("shop.refunds_show_all", { count: stats.refunds.length })}
                  </button>
                )}
              </div>

              {STRIPE_ONLINE_PAYMENTS && (
              <div className="app-card p-4 space-y-3" data-testid="treasury-payments">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="t-secondary text-xs uppercase tracking-wider">
                    {t("shop.online_payments")}
                    <span className={`ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${isLive ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-sky-500/30 bg-sky-500/10 text-sky-400"}`} data-testid="stripe-mode-badge">{isLive ? t("shop.live_mode_badge") : t("shop.test_mode_badge")}</span>
                  </p>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${payStatus?.enabled ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-amber-500/30 bg-amber-500/10 text-amber-400"}`} data-testid="pay-status-badge">
                    {payStatus?.enabled ? (payStatus?.mode === "platform_test" ? t("shop.pay_test_shared") : t("shop.pay_active")) : payStatus?.connected ? t("shop.pay_pending") : t("shop.pay_none")}
                  </span>
                </div>
                <p className="text-xs t-secondary">{t(isLive ? "shop.pay_hint_live" : "shop.pay_hint")}</p>
                <Button variant="outline" className="border-app" onClick={doConnect} data-testid="pay-connect-btn">
                  {payStatus?.connected ? t("shop.pay_resume") : t("shop.pay_connect")}
                </Button>
              </div>
              )}

              {!isLive && STRIPE_ONLINE_PAYMENTS && (
                <div className="app-card p-4 space-y-2" data-testid="prod-readiness-card">
                  <p className="t-secondary text-xs uppercase tracking-wider">{t("shop.prod_title")}</p>
                  <p className="text-xs t-secondary">{t("shop.prod_intro")}</p>
                  <ol className="text-xs t-secondary list-decimal list-inside space-y-1">
                    <li>{t("shop.prod_step1")}</li>
                    <li>{t("shop.prod_step2")}</li>
                    <li>{t("shop.prod_step3")}</li>
                    <li>{t("shop.prod_step4")}</li>
                  </ol>
                  <p className="text-[11px] t-secondary italic">{t("shop.prod_guide")}</p>
                </div>
              )}

              <a href={exportHref()} download className="inline-flex" data-testid="treasury-export">
                <Button variant="outline" className="border-app" type="button">
                  <Download className="h-4 w-4 mr-1" /> {t("shop.export_csv")}
                </Button>
              </a>
            </>
          )}
        </div>
      )}

      {/* ---------- GESTION (admin / trésorier) ---------- */}
      {mode === "manage" && canManage && (
        <div className="space-y-3" data-testid="shop-manage">
          <Button className="brand-bg text-white" onClick={() => setCatForm({ editing: null, name: "", icon: "package", allow_tab: true, allow_immediate_payment: true })} data-testid="shop-cat-new">
            <Plus className="h-4 w-4 mr-1" /> {t("shop.cat_new")}
          </Button>
          {categories.map((c, idx) => (
            <div key={c.id} className={`app-card p-4 ${!c.active ? "opacity-60" : ""}`} data-testid={`manage-cat-${c.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                {(() => { const Icon = SHOP_ICONS[c.icon] || Package; return <Icon className="h-6 w-6 accent shrink-0" />; })()}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">
                    {c.name}
                    {!c.active && <span className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-app t-secondary">{t("shop.inactive_badge")}</span>}
                  </p>
                  <p className="text-xs t-secondary">
                    {t("shop.allow_tab")} : {c.allow_tab ? t("shop.yes") : t("shop.no")} • {t("shop.allow_immediate")} : {c.allow_immediate_payment ? t("shop.yes") : t("shop.no")}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button className="p-2 t-secondary hover:text-white" onClick={() => setQrTarget({ name: c.name, link: `${window.location.origin}/achats?cat=${c.id}` })} data-testid={`cat-qr-${c.id}`} aria-label={t("shop.qr_code")} title={t("shop.qr_code")}><QrCode className="h-4 w-4" /></button>
                  <button className="p-2 t-secondary hover:text-white disabled:opacity-30" disabled={idx === 0} onClick={() => moveCategory(idx, -1)} data-testid={`cat-up-${c.id}`} aria-label="Monter"><ArrowUp className="h-4 w-4" /></button>
                  <button className="p-2 t-secondary hover:text-white disabled:opacity-30" disabled={idx === categories.length - 1} onClick={() => moveCategory(idx, 1)} data-testid={`cat-down-${c.id}`} aria-label="Descendre"><ArrowDown className="h-4 w-4" /></button>
                  <button className="p-2 t-secondary hover:text-white" onClick={() => setCatForm({ editing: c, name: c.name, icon: c.icon, allow_tab: c.allow_tab, allow_immediate_payment: c.allow_immediate_payment })} data-testid={`cat-edit-${c.id}`} aria-label={t("shop.cat_edit")}><Pencil className="h-4 w-4" /></button>
                  <button className="p-2 t-secondary hover:text-amber-400" onClick={() => toggleCategory(c)} data-testid={`cat-toggle-${c.id}`} aria-label={c.active ? t("shop.inactive_badge") : t("shop.yes")} title={c.active ? t("shop.inactive_badge") : t("shop.yes")}>
                    {c.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button className="p-2 t-secondary hover:text-rose-400" onClick={() => setDelTarget({ type: "category", obj: c })} data-testid={`cat-delete-${c.id}`} aria-label={t("common.delete")}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>

              {/* Articles de la catégorie */}
              <div className="mt-3 border-t border-app pt-3 space-y-2">
                {c.items.map((item, iidx) => (
                  <div key={item.id} className={`flex items-center gap-x-3 gap-y-1 flex-wrap text-sm ${!item.active ? "opacity-50" : ""}`} data-testid={`manage-item-${item.id}`}>
                    <span className="min-w-0 basis-full sm:basis-auto sm:flex-1 break-words">{item.name}</span>
                    <span className="t-secondary shrink-0">{euro(item.price_cents)}</span>
                    <button className="p-1.5 t-secondary hover:text-white disabled:opacity-30" disabled={iidx === 0} onClick={() => moveItem(c, iidx, -1)} data-testid={`item-up-${item.id}`} aria-label="Monter"><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button className="p-1.5 t-secondary hover:text-white disabled:opacity-30" disabled={iidx === c.items.length - 1} onClick={() => moveItem(c, iidx, 1)} data-testid={`item-down-${item.id}`} aria-label="Descendre"><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button className="p-1.5 t-secondary hover:text-white" onClick={() => setItemForm({ editing: item, category_id: c.id, name: item.name, description: item.description || "", price: (item.price_cents / 100).toFixed(2), allow_tab: item.allow_tab === null || item.allow_tab === undefined ? "inherit" : item.allow_tab ? "yes" : "no", active: item.active, image: null })} data-testid={`item-edit-${item.id}`} aria-label={t("shop.item_edit")}><Pencil className="h-3.5 w-3.5" /></button>
                    <button className="p-1.5 t-secondary hover:text-rose-400" onClick={() => setDelTarget({ type: "item", obj: item })} data-testid={`item-delete-${item.id}`} aria-label={t("common.delete")}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button className="text-xs brand hover:underline flex items-center gap-1 pt-1" onClick={() => setItemForm({ editing: null, category_id: c.id, name: "", description: "", price: "", allow_tab: "inherit", active: true, image: null })} data-testid={`item-new-${c.id}`}>
                  <Plus className="h-3.5 w-3.5" /> {t("shop.item_new")}
                </button>
              </div>
            </div>
          ))}

          {/* ---------- Cagnottes : gestion trésorier (Phase 4) ---------- */}
          <div className="border-t border-app pt-4 mt-2 space-y-3" data-testid="manage-pots">
            <p className="font-heading text-lg font-bold flex items-center gap-2">
              <PiggyBank className="h-5 w-5 accent" /> {t("shop.pots_title")}
            </p>
            <Button className="brand-bg text-white" onClick={() => setPotForm({ editing: null, name: "", description: "", goal: "" })} data-testid="pot-new">
              <Plus className="h-4 w-4 mr-1" /> {t("shop.pot_new")}
            </Button>
            {(pots || []).length === 0 && <p className="t-secondary text-sm" data-testid="pots-empty">{t("shop.pot_empty")}</p>}
            {(pots || []).map((pot) => (
              <div key={pot.id} className={`app-card p-4 flex items-center gap-3 flex-wrap ${!pot.active ? "opacity-60" : ""}`} data-testid={`manage-pot-${pot.id}`}>
                <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                  <p className="font-semibold break-words">
                    {pot.name}
                    {!pot.active && <span className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-app t-secondary">{t("shop.pot_closed")}</span>}
                  </p>
                  <p className="text-xs t-secondary">
                    {t("shop.pot_collected", { amount: euro(pot.collected_cents) })}
                    {pot.goal_cents ? ` • ${t("shop.pot_goal_label", { amount: euro(pot.goal_cents) })}` : ` • ${t("shop.pot_free")}`}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button className="p-2 t-secondary hover:text-white" onClick={() => setQrTarget({ name: pot.name, link: `${window.location.origin}/achats?pot=${pot.id}` })} data-testid={`pot-qr-${pot.id}`} aria-label={t("shop.qr_code")} title={t("shop.qr_code")}><QrCode className="h-4 w-4" /></button>
                  <button className="p-2 t-secondary hover:text-white" onClick={() => setPotForm({ editing: pot, name: pot.name, description: pot.description || "", goal: pot.goal_cents ? (pot.goal_cents / 100).toFixed(2) : "" })} data-testid={`pot-edit-${pot.id}`} aria-label={t("shop.pot_edit")}><Pencil className="h-4 w-4" /></button>
                  <button className="p-2 t-secondary hover:text-amber-400" onClick={() => togglePot(pot)} data-testid={`pot-toggle-${pot.id}`} aria-label={pot.active ? t("shop.pot_closed") : t("shop.yes")} title={pot.active ? t("shop.pot_closed") : t("shop.yes")}>
                    {pot.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button className="p-2 t-secondary hover:text-rose-400" onClick={() => setDelTarget({ type: "pot", obj: pot })} data-testid={`pot-delete-${pot.id}`} aria-label={t("common.delete")}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------- Barre panier (ajout rapide à l'ardoise) ---------- */}
      {mode === "shop" && cartCount > 0 && (
        <div className="fixed bottom-20 sm:bottom-6 left-1/2 -translate-x-1/2 z-40" data-testid="shop-cart-bar">
          <div className="app-card px-5 py-3 flex items-center gap-4 shadow-xl">
            <span className="text-sm font-semibold" data-testid="shop-cart-count">{t("shop.items_count", { count: cartCount })}</span>
            <span className="font-heading font-bold" data-testid="shop-cart-total">{euro(cartTotal)}</span>
            <Button className="brand-bg text-white" disabled={cartBusy} onClick={submitCart} data-testid="shop-cart-validate">
              {t("shop.cart_validate")}
            </Button>
          </div>
        </div>
      )}

      {/* ---------- Dialog QR code de catégorie (Phase 3) ---------- */}
      <Dialog open={!!qrTarget} onOpenChange={(v) => !v && setQrTarget(null)}>
        <DialogContent className="card-bg border-app max-w-sm" data-testid="qr-dialog">
          {qrTarget && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{t("shop.qr_title", { name: qrTarget.name })}</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">{t("shop.qr_code")}</DialogDescription>
              <QrPanel target={qrTarget} />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Dialog catégorie ---------- */}
      <Dialog open={!!catForm} onOpenChange={(v) => !v && setCatForm(null)}>
        <DialogContent className="card-bg border-app max-w-md" data-testid="cat-form-dialog" aria-describedby={undefined}>
          {catForm && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{catForm.editing ? t("shop.cat_edit") : t("shop.cat_new")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="cat-name-input">{t("shop.cat_name")}</Label>
                  <Input id="cat-name-input" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="cat-name-input" />
                </div>
                <div>
                  <p className="t-secondary text-xs uppercase tracking-wider mb-2">{t("shop.icon")}</p>
                  <div className="grid grid-cols-8 gap-1.5">
                    {Object.entries(SHOP_ICONS).map(([key, Icon]) => (
                      <button key={key} type="button" onClick={() => setCatForm({ ...catForm, icon: key })} className={`p-2.5 rounded-xl border transition-colors ${catForm.icon === key ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`} data-testid={`cat-icon-${key}`} aria-label={key}>
                        <Icon className="h-4 w-4 mx-auto" />
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" className="accent-emerald-500 h-4 w-4" checked={catForm.allow_tab} onChange={(e) => setCatForm({ ...catForm, allow_tab: e.target.checked })} data-testid="cat-allow-tab" />
                  {t("shop.allow_tab")}
                </label>
                <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" className="accent-emerald-500 h-4 w-4" checked={catForm.allow_immediate_payment} onChange={(e) => setCatForm({ ...catForm, allow_immediate_payment: e.target.checked })} data-testid="cat-allow-immediate" />
                  {t("shop.allow_immediate")}
                </label>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" className="flex-1 border-app" onClick={() => setCatForm(null)} data-testid="cat-cancel">{t("confirm.cancel")}</Button>
                  <Button className="flex-1 brand-bg text-white" onClick={saveCategory} disabled={saving || !catForm.name.trim()} data-testid="cat-save-button">{t("common.save")}</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Dialog article ---------- */}
      <Dialog open={!!itemForm} onOpenChange={(v) => !v && setItemForm(null)}>
        <DialogContent className="card-bg border-app max-w-md" data-testid="item-form-dialog" aria-describedby={undefined}>
          {itemForm && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{itemForm.editing ? t("shop.item_edit") : t("shop.item_new")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="item-name-input">{t("shop.item_name")}</Label>
                  <Input id="item-name-input" value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="item-name-input" />
                </div>
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="item-desc-input">{t("shop.item_desc")}</Label>
                  <Input id="item-desc-input" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="item-desc-input" />
                </div>
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="item-price-input">{t("shop.price")}</Label>
                  <Input id="item-price-input" type="number" min="0" step="0.50" inputMode="decimal" value={itemForm.price} onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="item-price-input" />
                </div>
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="item-image-input">{t("shop.image")}</Label>
                  <input id="item-image-input" type="file" accept="image/*" className="mt-1 text-sm t-secondary" onChange={(e) => setItemForm({ ...itemForm, image: e.target.files?.[0] || null })} data-testid="item-image-input" />
                </div>
                <div>
                  <p className="t-secondary text-xs uppercase tracking-wider mb-1.5">{t("shop.item_tab_mode")}</p>
                  <div className="flex gap-1.5">
                    {[["inherit", t("shop.tab_inherit")], ["yes", t("shop.yes")], ["no", t("shop.no")]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setItemForm({ ...itemForm, allow_tab: v })} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${itemForm.allow_tab === v ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`} data-testid={`item-tab-${v}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" className="accent-emerald-500 h-4 w-4" checked={itemForm.active} onChange={(e) => setItemForm({ ...itemForm, active: e.target.checked })} data-testid="item-active" />
                  {t("shop.item_active")}
                </label>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" className="flex-1 border-app" onClick={() => setItemForm(null)} data-testid="item-cancel">{t("confirm.cancel")}</Button>
                  <Button className="flex-1 brand-bg text-white" onClick={saveItem} disabled={saving || !itemForm.name.trim() || itemForm.price === ""} data-testid="item-save-button">{t("common.save")}</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Dialog encaissement ---------- */}
      <Dialog open={!!settleTarget} onOpenChange={(v) => !v && setSettleTarget(null)}>
        <DialogContent className="card-bg border-app max-w-md" data-testid="settle-dialog">
          {settleTarget && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{t("shop.settle")} — {settleTarget.member_name}</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">{t("shop.settle")}</DialogDescription>
              <div className="space-y-4">
                <p className="text-sm t-secondary">
                  {t("shop.remaining")} : <span className="font-semibold text-white" data-testid="settle-remaining">{euro(settleTarget.remaining_cents)}</span>
                </p>
                <div>
                  <p className="t-secondary text-xs uppercase tracking-wider mb-1.5">{t("shop.settle_method")}</p>
                  <div className="flex gap-1.5">
                    {SETTLE_METHODS.map((m) => (
                      <button key={m} type="button" onClick={() => setSettleForm({ ...settleForm, method: m })} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${settleForm.method === m ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`} data-testid={`settle-method-${m.toLowerCase()}`}>
                        {t(`shop.method_${m.toLowerCase()}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="settle-amount-input">{t("shop.settle_amount")}</Label>
                  <Input id="settle-amount-input" type="number" min="0" step="0.50" inputMode="decimal" value={settleForm.amount} onChange={(e) => setSettleForm({ ...settleForm, amount: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="settle-amount-input" />
                </div>
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="settle-note-input">{t("shop.settle_note")}</Label>
                  <Input id="settle-note-input" value={settleForm.note} onChange={(e) => setSettleForm({ ...settleForm, note: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="settle-note-input" />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" className="flex-1 border-app" onClick={() => setSettleTarget(null)} data-testid="settle-cancel">{t("confirm.cancel")}</Button>
                  <Button className="flex-1 brand-bg text-white" onClick={doSettle} disabled={saving || !settleForm.amount} data-testid="settle-confirm">{t("common.save")}</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Dialog annulation tracée ---------- */}
      <Dialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <DialogContent className="card-bg border-app max-w-md" data-testid="cancel-dialog">
          {cancelTarget && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{t("shop.cancel_purchase")} — {euro(cancelTarget.total_cents)}</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">{t("shop.cancel_purchase")}</DialogDescription>
              <div className="space-y-4">
                <p className="text-xs t-secondary">{t("shop.cancel_hint")}</p>
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="cancel-reason-input">{t("shop.cancel_reason")}</Label>
                  <Input id="cancel-reason-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="mt-1 bg-transparent border-app" data-testid="cancel-reason-input" />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" className="flex-1 border-app" onClick={() => setCancelTarget(null)} data-testid="cancel-abort">{t("confirm.cancel")}</Button>
                  <Button className="flex-1 bg-rose-600 hover:bg-rose-500 text-white" onClick={doCancel} disabled={saving || !cancelReason.trim()} data-testid="cancel-confirm">{t("shop.cancel_purchase")}</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Dialog contribution à une cagnotte (Phase 4) ---------- */}
      <Dialog open={!!contribPot} onOpenChange={(v) => !v && setContribPot(null)}>
        <DialogContent className="card-bg border-app max-w-md" data-testid="contribute-dialog">
          {contribPot && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{t("shop.pot_contribute")} — {contribPot.name}</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">{t("shop.pot_contribute")}</DialogDescription>
              <div className="space-y-4">
                {contribPot.goal_cents ? (
                  <p className="text-sm t-secondary">
                    {t("shop.pot_collected", { amount: euro(contribPot.collected_cents) })} • {t("shop.pot_goal_label", { amount: euro(contribPot.goal_cents) })}
                  </p>
                ) : null}
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="contribute-amount-input">{t("shop.pot_amount")}</Label>
                  <Input id="contribute-amount-input" type="number" min="0" step="0.50" inputMode="decimal" value={contribAmount} onChange={(e) => setContribAmount(e.target.value)} className="mt-1 bg-transparent border-app" data-testid="contribute-amount-input" />
                </div>
                <p className="text-xs t-secondary">{t("shop.pot_tab_hint")}</p>
                <div className="flex gap-2 pt-1 flex-wrap">
                  <Button variant="outline" className="flex-1 border-app min-w-28" onClick={() => setContribPot(null)} data-testid="contribute-cancel">{t("confirm.cancel")}</Button>
                  <Button className="flex-1 brand-bg text-white min-w-28" onClick={submitContrib} disabled={contribBusy || !contribAmount} data-testid="contribute-confirm">{t("shop.pot_contribute")}</Button>
                  {STRIPE_ONLINE_PAYMENTS && payStatus?.enabled && (
                    <Button className="flex-1 bg-sky-600 hover:bg-sky-500 text-white min-w-28" onClick={payPotOnline} disabled={contribBusy || !contribAmount} data-testid="contribute-pay-online">{t(isLive ? "shop.pay_online_live" : "shop.pay_online")}</Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Dialog cagnotte (Phase 4) ---------- */}
      <Dialog open={!!potForm} onOpenChange={(v) => !v && setPotForm(null)}>
        <DialogContent className="card-bg border-app max-w-md" data-testid="pot-form-dialog">
          {potForm && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{potForm.editing ? t("shop.pot_edit") : t("shop.pot_new")}</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">{t("shop.pot_new")}</DialogDescription>
              <div className="space-y-4">
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="pot-name-input">{t("shop.pot_name")}</Label>
                  <Input id="pot-name-input" value={potForm.name} onChange={(e) => setPotForm({ ...potForm, name: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="pot-name-input" />
                </div>
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="pot-desc-input">{t("shop.pot_desc")}</Label>
                  <Input id="pot-desc-input" value={potForm.description} onChange={(e) => setPotForm({ ...potForm, description: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="pot-desc-input" />
                </div>
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider" htmlFor="pot-goal-input">{t("shop.pot_goal")}</Label>
                  <Input id="pot-goal-input" type="number" min="0" step="1" inputMode="decimal" value={potForm.goal} onChange={(e) => setPotForm({ ...potForm, goal: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="pot-goal-input" />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" className="flex-1 border-app" onClick={() => setPotForm(null)} data-testid="pot-cancel">{t("confirm.cancel")}</Button>
                  <Button className="flex-1 brand-bg text-white" onClick={savePot} disabled={saving || !potForm.name.trim()} data-testid="pot-save-button">{t("common.save")}</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Dialog paiement sur place (Phase 7) ---------- */}
      <Dialog open={onsiteOpen} onOpenChange={(v) => !v && closeOnsite()}>
        <DialogContent className="card-bg border-app max-w-md max-h-[85vh] overflow-y-auto" data-testid="onsite-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {t("shop.onsite")}
              <span className={`ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full border align-middle ${isLive ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-sky-500/30 bg-sky-500/10 text-sky-400"}`} data-testid="stripe-mode-badge-onsite">{isLive ? t("shop.live_mode_badge") : t("shop.test_mode_badge")}</span>
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="sr-only">{t("shop.onsite")}</DialogDescription>
          {!onsiteTx ? (
            <div className="space-y-3">
              <p className="text-xs t-secondary">{t("shop.onsite_hint")}</p>
              {categories.map((c) => c.items.filter((i) => i.active).map((item) => (
                <div key={item.id} className="flex items-center gap-3 text-sm" data-testid={`onsite-item-${item.id}`}>
                  <span className="min-w-0 flex-1 break-words">{item.name} <span className="t-secondary">({euro(item.price_cents)})</span></span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button className="p-1.5 rounded-full border border-app t-secondary hover:text-white disabled:opacity-30" disabled={!onsiteCart[item.id]} onClick={() => onsiteBump(item.id, -1)} data-testid={`onsite-minus-${item.id}`} aria-label="−">
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-5 text-center text-sm font-semibold" data-testid={`onsite-qty-${item.id}`}>{onsiteCart[item.id] || 0}</span>
                    <button className="p-1.5 rounded-full brand-bg text-white" onClick={() => onsiteBump(item.id, 1)} data-testid={`onsite-plus-${item.id}`} aria-label="+">
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )))}
              <div className="flex items-center justify-between pt-2 border-t border-app">
                <span className="font-semibold">{t("shop.total")}</span>
                <span className="font-heading font-bold text-lg" data-testid="onsite-total">{euro(onsiteTotal())}</span>
              </div>
              <Button className="w-full brand-bg text-white" disabled={!onsiteLines.length} onClick={generateOnsite} data-testid="onsite-generate">
                <QrCode className="h-4 w-4 mr-1.5" /> {t("shop.onsite_show_qr")}
              </Button>
            </div>
          ) : (
            <div className="space-y-4 text-center">
              <p className="font-heading font-bold text-2xl" data-testid="onsite-amount">{euro(onsiteTx.amount_cents)}</p>
              <div className="bg-white rounded-2xl p-4 inline-block overflow-hidden">
                <canvas ref={onsiteQrRef} style={{ width: 240, height: 240 }} data-testid="onsite-qr-canvas" />
              </div>
              <p className="text-xs t-secondary">{t("shop.onsite_scan")}</p>
              <p className={`text-sm font-semibold ${onsitePaid ? "text-emerald-400" : "t-secondary"}`} data-testid="onsite-status">
                {onsitePaid ? t("shop.onsite_paid") : t("shop.onsite_waiting")}
              </p>
              <div className="flex gap-2">
                {onsitePaid && (
                  <Button className="flex-1 brand-bg text-white" onClick={newOnsiteSale} data-testid="onsite-new">{t("shop.onsite_new")}</Button>
                )}
                <Button variant="outline" className="flex-1 border-app" onClick={closeOnsite} data-testid="onsite-close">
                  {onsitePaid ? t("shop.onsite_close") : t("shop.onsite_abandon")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delTarget}
        title={t("common.delete")}
        description={delTarget ? t(delTarget.type === "category" ? "shop.delete_cat_desc" : delTarget.type === "pot" ? "shop.delete_pot_desc" : "shop.delete_item_desc", { name: delTarget.obj.name }) : ""}
        onConfirm={doDelete}
        onClose={() => setDelTarget(null)}
        testid="delete-purchase-confirm-dialog"
      />
    </div>
  );
}
